
import Vue from 'vue'
import Component from 'vue-class-component'
import {State} from 'vuex-class'
import {Prop} from 'vue-property-decorator'

import TempAccount from '@/models/tempAccount'
import Service from '@/models/service'
import BlueButton from '../Atomic/BlueButton.vue'
import GreenButton from '../Atomic/GreenButton.vue'
import RedButton from '../Atomic/RedButton.vue'
import TempAccountService from '@/services/TempAccountService'
import ServiceService from '@/services/ServiceService'
import ErrorHandler from '@/services/ErrorHandlerService'
import NotificationService, {
  INotificationService,
} from '@/services/NotificationService'
import DatePicker from '@/components/Atomic/DatePicker.vue'
import CardTitle from '@/components/Atomic/CardTitle.vue'
import ProgressLinear from '@/components/Atomic/ProgressLinear.vue'

const Props = Vue.extend({
  props: {
    onClose: Function,
  },
})

@Component({
  components: {
    DatePicker,
    BlueButton,
    GreenButton,
    RedButton,
    CardTitle,
    ProgressLinear,
  },
})
export default class TempAccountUpdate extends Props {
  @State('user') private currentUser!: any
  @Prop() private readonly descriptions!: string[]
  private dialog: boolean = false
  private isPending: boolean = false
  private stepper: number = 1

  private validEmail: boolean = false
  private validatingEmailAddress: boolean = false
  private lastCheckedEmail: string = ''
  private emailExists: boolean = false

  private tempAccounts: TempAccount[] = [] as TempAccount[]
  private tempAccount: TempAccount = {} as TempAccount
  private services: Service[] = []
  private selectedSchool: string | null = null

  private phoneNumberRegExp = new RegExp(/^[\d\s]{10,19}$/)

  private $notificationService!: INotificationService
  private errorHandler = new ErrorHandler()
  private enabledService: number[] = []
  private title: string[] = ['Monsieur', 'Madame']

  public show(
      tempAccounts: TempAccount[],
      tempAccount: TempAccount,
      step: number,
  ) {
    this.tempAccounts = tempAccounts
    this.tempAccount = Object.assign({}, tempAccount)
    this.reset()
    this.fetchServices()
    this.stepper = step || this.stepper
    this.dialog = true
  }

  private formatDate(date: Date | string | undefined) {
    return date === undefined ? '' : (this as any).$moment(date).format('L')
  }

  private isGroupEnabled(groupID: number) {
    return this.enabledService.includes(groupID)
  }

  private get hasSchoolSpecificService() {
    return this.services.some((s) => s.school_specific)
  }

  private get hasSchoolSpecificServiceSelected() {
    const schoolSpecficService: number[] = this.services
        .filter((s) => s.school_specific)
        .map((s) => s.groupe_ad_id)
    return schoolSpecficService.some((s) => this.enabledService.includes(s))
  }

  private async reset() {
    this.isPending = true
    this.validEmail = false
    try {
      this.enabledService = []
      const details = await TempAccountService.getDetails(
          this.tempAccount.ad_personne_id,
      )
      // Enable the services already enabled for this user
      details.services.forEach((enabledService: any) => {
        this.enabledService.push(enabledService)
      })
      if (details.services_school) {
        this.selectedSchool = details.services_school[0]
      } else {
        this.selectedSchool = null
      }
    } catch (error: any) {
      if (error.response !== undefined) {
        this.errorHandler.handle(this, error.response.data.code, true)
        this.hide()
      }
    } finally {
      this.isPending = false
    }
    this.isPending = false
  }

  private hide() {
    this.onClose()
    this.dialog = false
    this.tempAccount = {} as TempAccount
    this.enabledService = []
    this.selectedSchool = null
    this.stepper = 1
  }

  private async next() {
    const validated = await this.$validator.validateAll()
    if (validated) {
      this.stepper++
    }
  }

  private previous() {
    this.stepper--
  }

  private updateEnabledServices(serviceID: number) {
    if (this.enabledService.includes(serviceID)) {
      this.enabledService.splice(this.enabledService.indexOf(serviceID), 1)
    } else {
      this.enabledService.push(serviceID)
    }
    if (!this.hasSchoolSpecificServiceSelected) {
      this.selectedSchool = null
    }
  }

  private async checkExistingMail() {
    const email = this.tempAccount.courriel_autre
    this.validEmail = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(
        this.tempAccount.courriel_autre,
    )
    if (this.validEmail === true) {
      if (email !== this.lastCheckedEmail) {
        this.lastCheckedEmail = email
        this.validatingEmailAddress = true
        const MailErrorField = this.$t('Email').toString()
        try {
          this.emailExists = await TempAccountService.checkForExistingMailInUpdate(
              this.tempAccount.courriel_autre,
              this.tempAccount.ad_personne_id,
          )
          this.emailExists = true

          const emailErrorMessage = this.$t(
              'TEMPACCOUNT.CreateTempAccountEmailAlreadyTaken',
          ).toString()
          this.$validator.errors.add({
            field: MailErrorField,
            msg: emailErrorMessage,
            rule: 'unique',
          })
        } catch (error: any) {
          this.$validator.errors.remove(MailErrorField)
          this.emailExists = false
        } finally {
          this.validatingEmailAddress = false
        }
      }
    }
  }

  private get existingEmailIcon() {
    const icon = 'mdi-email'
    const append =
        this.validEmail && !this.isPending
            ? this.emailExists
            ? '-alert'
            : '-check'
            : ''
    return icon + append
  }

  private get existingEmailIconColor() {
    return this.validEmail && !this.isPending
        ? this.emailExists
            ? 'error'
            : 'success'
        : ''
  }

  private async update() {
    if (this.hasSchoolSpecificServiceSelected && this.selectedSchool === null) {
      this.errorHandler.handle(this, 'toto', true)
      return
    }
    this.isPending = true
    try {
      const updatedTempAccount = await TempAccountService.updateTempAccount(
          this.tempAccount,
          this.enabledService,
          this.currentUser.roles,
          'Basique',
          this.selectedSchool,
      )
      const index = this.tempAccounts.findIndex(
          (ta) => ta.ad_personne_id === updatedTempAccount.ad_personne_id,
      )
      Vue.set(this.tempAccounts, index, updatedTempAccount)

      this.$notificationService.success(
          this.$t('TEMPACCOUNT.UpdateTempAccountSuccess').toString(),
      )
      this.hide()
    } catch (error: any) {
      if (error.response !== undefined) {
        this.errorHandler.handle(this, error.response.data.code, true)
      }
    } finally {
      this.isPending = false
    }
    this.isPending = false
  }

  private async fetchServices() {
    this.isPending = true
    try {
      this.services = await ServiceService.availableServices(
          this.currentUser.roles,
          'Basique',
      )
      this.services.sort((a, b) =>
          a.school_specific === true && b.school_specific === false ? 1 : -1,
      )
    } catch (error: any) {
      if (error.response !== undefined) {
        this.errorHandler.handle(this, error.response.data.code, true)
      }
    } finally {
      this.isPending = false
    }
  }
}
